import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resolveAmlFrameworks } from './amlApi';

// Shared across all four AML pages: resolves every AML framework the org matches (plural —
// every AML table is framework-scoped as of 2026-10-08) and tracks which one is "active" via a
// ?frameworkId= URL query param, so switching framework survives navigating between Overview /
// Risk Assessment / Customer Segments / Filings (AmlNav carries the param on every tab link).
// Defaults to the first resolved framework when the org only matches one — the common case looks
// and behaves exactly as it did before multi-framework support existed.
export default function useAmlFramework() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [frameworks, setFrameworks] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'not-enrolled' | 'error'

  useEffect(() => {
    let alive = true;
    resolveAmlFrameworks()
      .then((list) => {
        if (!alive) return;
        setFrameworks(list);
        setStatus(list.length === 0 ? 'not-enrolled' : 'ok');
      })
      .catch(() => { if (alive) setStatus('error'); });
    return () => { alive = false; };
  }, []);

  const urlId = searchParams.get('frameworkId');
  const parsedUrlId = urlId ? Number(urlId) : null;
  const frameworkId = frameworks.some((f) => f.id === parsedUrlId) ? parsedUrlId : (frameworks[0]?.id ?? null);
  const framework = frameworks.find((f) => f.id === frameworkId) || null;

  // Keep the URL in sync once frameworks resolve, so AmlNav's tab links (which read the URL
  // directly) always carry a real id, not a missing/stale one.
  useEffect(() => {
    if (frameworkId == null) return;
    if (parsedUrlId === frameworkId) return;
    const next = new URLSearchParams(searchParams);
    next.set('frameworkId', String(frameworkId));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameworkId]);

  const setFrameworkId = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    next.set('frameworkId', String(id));
    setSearchParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return { status, frameworks, frameworkId, framework, setFrameworkId };
}
